import getPageInfo from './get-page-info';

/**
 * Which Studio a person is looking at, as their events report it:
 * - `new`: the revamp, the tenant has `studio-tree-insights` on;
 * - `classic`: the tenant does not have the revamp;
 * - `classic_by_choice`: the tenant has it and this browser chose the classic
 *   one with the experience toggle (`legacy-mode.ts`).
 *
 * It describes the setting, not the page: an engagement model's pages are
 * classic in every case, so a reader pairs it with `model_type`.
 */
export type StudioExperience = 'new' | 'classic' | 'classic_by_choice';

/**
 * Per tenant, because one tab can move between tenants and each has its own
 * flag. Filled when the flag resolves (`fetchTreeInsightsEnabled`); an event
 * fired before that carries no experience rather than a guess.
 */
const experienceByTenant = new Map<string, StudioExperience>();

export function recordStudioExperience(
  tenant: string,
  experience: StudioExperience
) {
  experienceByTenant.set(tenant, experience);
}

/** For tests: forget what was recorded. */
export function clearStudioExperience() {
  experienceByTenant.clear();
}

const TENANT_IN_PATH = /^\/tenant\/([^/]+)/;

/** The experience recorded for the tenant a path is under, if any. */
function experienceFor(pathname: string): StudioExperience | undefined {
  const tenant = TENANT_IN_PATH.exec(pathname)?.[1];
  return tenant ? experienceByTenant.get(tenant) : undefined;
}

/**
 * The page an event happened on: `path`, the name admin's events use, and
 * `page_name`, Studio's own page taxonomy, the one the page views carry.
 */
export function pageProperties(pathname: string) {
  return {
    path: pathname,
    page_name: getPageInfo(pathname)?.pageName ?? null,
  };
}

/**
 * What every Studio track event carries on top of its own properties: the app,
 * so one Amplitude chart reads admin's and Studio's events side by side, the
 * page, and once known the experience (`studio_experience`). A value the event sets itself wins, which is how a chat turn keeps the
 * page it was sent from.
 *
 * Not `url` or `referrer`: Segment already sends both under `context.page`, and
 * the full URL would copy query strings (filters, the OAuth `code`) into the
 * properties of every event.
 */
export function withStudioDefaults(
  properties: Record<string, unknown> | undefined,
  pathname: string
) {
  const experience = experienceFor(pathname);
  return {
    app: 'studio',
    ...pageProperties(pathname),
    ...(experience ? { studio_experience: experience } : {}),
    ...(properties ?? {}),
  };
}

export interface SourceMiddlewareContext {
  payload: {
    obj: { type?: string; properties?: Record<string, unknown> };
  };
  next: (payload: SourceMiddlewareContext['payload']) => void;
}

/**
 * A Segment source middleware applying `withStudioDefaults` to every track call,
 * whoever makes it: the store, the guides forwarder, anything added later.
 */
export function studioEventDefaults(pathname: () => string) {
  return ({ payload, next }: SourceMiddlewareContext) => {
    if (payload.obj.type === 'track') {
      payload.obj.properties = withStudioDefaults(
        payload.obj.properties,
        pathname()
      );
    }
    next(payload);
  };
}
